import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/constants";
import { api, BACKEND_URL } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { getGdpr } from "@/i18n/gdpr";

const PARTNERS_LABEL = {
  fr: "Partenaires", en: "Partners", es: "Socios", de: "Partner", it: "Partner",
  pt: "Parceiros", ru: "Партнёры", ja: "パートナー", hi: "साझेदार", ar: "الشركاء", zh: "合作伙伴",
};
const CAND_LABEL = {
  fr: "Espace candidat", en: "Candidate area", es: "Área de candidato", de: "Kandidatenbereich",
  it: "Area candidato", pt: "Área do candidato", ru: "Кабинет кандидата", ja: "候補者エリア",
  hi: "उम्मीदवार क्षेत्र", ar: "منطقة المرشح", zh: "候选人专区",
};

export const Footer = () => {
  const { t, lang } = useI18n();
  const g = getGdpr(lang);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/partners").then(({ data }) => setPartners(data)).catch(() => setPartners([]));
  }, []);

  return (
    <footer data-testid="main-footer" className="relative border-t border-theatre-gold/20 bg-black py-16 px-6 lg:px-10">
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10 items-start">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="Viva Artist" className="h-12 w-12 object-contain" />
            <span className="font-serif text-xl text-white">VIVA <span className="text-theatre-gold">ARTIST</span></span>
          </div>
          <p className="mt-5 text-sm text-neutral-500 leading-relaxed max-w-md">{t("footer.tagline")}</p>
          <p className="mt-4 text-sm text-neutral-400 leading-relaxed max-w-md">{t("footer.projectText")}</p>
        </div>

        <div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-theatre-gold mb-4">{t("footer.espace")}</p>
          <div className="flex flex-col gap-2.5">
            <Link to="/espace-candidat" data-testid="footer-candidate-link" className="text-sm text-neutral-400 hover:text-theatre-gold transition-colors duration-200">
              {CAND_LABEL[lang] || CAND_LABEL.fr}
            </Link>
            <Link to="/admin/login" data-testid="footer-admin-link" className="text-sm text-neutral-400 hover:text-theatre-gold transition-colors duration-200">
              {t("footer.adminLink")}
            </Link>
          </div>
        </div>

        <div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-theatre-gold mb-4">RGPD</p>
          <div className="flex flex-col gap-2.5">
            <Link to="/confidentialite" data-testid="footer-privacy-link" className="text-sm text-neutral-400 hover:text-theatre-gold transition-colors duration-200">
              {g.privacy}
            </Link>
            <Link to="/confidentialite" data-testid="footer-legal-link" className="text-sm text-neutral-400 hover:text-theatre-gold transition-colors duration-200">
              {g.legal}
            </Link>
          </div>
        </div>
      </div>

      {partners.length > 0 && (
        <div className="max-w-7xl mx-auto mt-14 pt-10 border-t border-white/5" data-testid="footer-partners">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-neutral-500 mb-6 text-center">
            {PARTNERS_LABEL[lang] || PARTNERS_LABEL.fr}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {partners.map((p) => {
              const inner = p.logo_url ? (
                <img src={`${BACKEND_URL}${p.logo_url}`} alt={p.name} title={p.name} className="h-12 md:h-14 w-auto object-contain opacity-70 hover:opacity-100 grayscale hover:grayscale-0 transition-all duration-300" />
              ) : (
                <span className="font-serif text-sm md:text-base text-neutral-300 hover:text-theatre-gold transition-colors duration-200 tracking-wide">{p.name}</span>
              );
              return p.url ? (
                <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`partner-${p.id}`} className="inline-flex items-center">
                  {inner}
                </a>
              ) : (
                <span key={p.id} data-testid={`partner-${p.id}`} className="inline-flex items-center">
                  {inner}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-4">
        <p className="text-xs text-neutral-600">{t("footer.rights")}</p>
        <p className="text-xs text-neutral-600">{t("footer.surprise")}</p>
      </div>
    </footer>
  );
};
